// Client hook: loads the library index once, runs fuzzy search in a Worker,
// and exposes decoded gems that update on every keystroke with zero latency.
//
// Speed notes:
//  - the index is persisted in Cache Storage, so repeat visits render from
//    disk instantly and revalidate in the background via ETag
//  - the worker answers with a transferable Int32Array of row indexes, and
//    gems are decoded once into a positional array

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { decodeRow } from "./library-types";
import type { LibraryGem, LibraryIndex, LibraryIndexRow, SortMode } from "./library-types";

const INDEX_URL = "/api/public/library";
const CACHE_NAME = "vibeary-index-v4";

type SearchState = {
  index: LibraryIndex | null;
  results: Int32Array;
  total: number;
  loading: boolean;
};

const EMPTY = new Int32Array(0);

async function readCachedIndex(): Promise<LibraryIndex | null> {
  try {
    if (typeof caches === "undefined") return null;
    const cache = await caches.open(CACHE_NAME);
    const hit = await cache.match(INDEX_URL);
    if (!hit) return null;
    return (await hit.json()) as LibraryIndex;
  } catch {
    return null;
  }
}

async function fetchAndCacheIndex(): Promise<LibraryIndex | null> {
  const res = await fetch(INDEX_URL);
  if (!res.ok) throw new Error("index fetch failed");
  try {
    if (typeof caches !== "undefined") {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(INDEX_URL, res.clone());
    }
  } catch {
    // Cache Storage may be unavailable (private mode) — not fatal.
  }
  return (await res.json()) as LibraryIndex;
}

export function useLibrarySearch(initialGems: LibraryGem[] = []) {
  const [state, setState] = useState<SearchState>({
    index: null,
    results: EMPTY,
    total: 0,
    loading: true,
  });
  const loading = state.loading;
  const workerRef = useRef<Worker | null>(null);
  const gems = useRef<LibraryGem[]>([]);
  const pendingSearch = useRef<{
    q: string;
    category: string | null;
    subcategory: string | null;
    sort: SortMode;
  } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    const worker = new Worker(new URL("./library-worker.ts", import.meta.url), {
      type: "module",
    });
    workerRef.current = worker;

    worker.onerror = (e: ErrorEvent) => {
      console.error("[library] worker error:", e.message, e.filename, e.lineno);
      setState((s) => ({ ...s, loading: false }));
    };

    worker.onmessage = (e: MessageEvent) => {
      const msg = e.data;
      if (msg.type === "ready") {
        setState((s) => ({ ...s, loading: false }));
        worker.postMessage({
          type: "search",
          q: "",
          category: null,
          subcategory: null,
          sort: "relevance",
          ...(pendingSearch.current ?? {}),
        });
        pendingSearch.current = null;
        return;
      }
      if (msg.type === "results") {
        setState((s) => ({ ...s, results: msg.ids as Int32Array, total: msg.total }));
      }
    };

    const apply = (index: LibraryIndex) => {
      if (cancelled) return;
      gems.current = (index.rows as LibraryIndexRow[]).map((row) =>
        decodeRow(row, index.cats, index.subs, index.imgBase),
      );
      setState((s) => ({ ...s, index }));
      worker.postMessage({
        type: "init",
        rows: index.rows,
        cats: index.cats,
        subs: index.subs,
      });
    };

    (async () => {
      const cached = await readCachedIndex();
      if (cached && !cancelled) apply(cached);
      try {
        const fresh = await fetchAndCacheIndex();
        // Only re-init when the payload actually changed.
        if (fresh && !cancelled && fresh.rows.length !== (cached?.rows.length ?? -1)) {
          apply(fresh);
        } else if (!cached && fresh) {
          apply(fresh);
        }
      } catch {
        if (!cached) setState((s) => ({ ...s, loading: false }));
      }
    })();

    return () => {
      cancelled = true;
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  // Stable identity: callers debounce on it, so a new function every render
  // would re-run the search on every state update (and reset pagination).
  const search = useCallback(
    (
      q: string,
      category: string | null,
      subcategory: string | null,
      sort: SortMode = "relevance",
    ) => {
      const w = workerRef.current;
      if (!w || loading) {
        pendingSearch.current = { q, category, subcategory, sort };
        return;
      }
      w.postMessage({ type: "search", q, category, subcategory, sort });
    },
    [loading],
  );

  const getGem = useMemo(
    () => (rowIndex: number) => gems.current[rowIndex] ?? null,
    [],
  );

  const ready = !loading && state.index !== null;

  return {
    index: state.index,
    results: state.results,
    total: ready ? state.total : initialGems.length,
    loading: state.loading,
    ready,
    search,
    getGem,
  };
}
